import { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import { useAuth } from "./AuthProvider";
import Environment from "../utility/environment";


interface Notification {
  id: string;
  title: string;
  description: string;
  date: string;
  viewed: boolean;
  link_url?: string | null;
}


const NotificationsContext = createContext<{
  notifications: Notification[];
  atualizar: () => Promise<void>;
  setViewed: (notification: Notification) => void;
}>({
  notifications: [],
  atualizar: async () => {},
  setViewed: () => {},
});

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user, accessToken } = useAuth();
  const pushRegistered = useRef(false);

  const getNotifications = useCallback(async () => {
    if (!accessToken || !user?.id) {
      return;
    }

    const res = await fetch(`${Environment.VITE_API_URL}/api/v1/get-notifications/${user.id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (!res.ok) {
      return;
    }

    const data = await res.json();

    const formatted: Notification[] = data.map((notification: any) => ({
      id: notification.id,
      title: notification.title,
      description: notification.description,
      date: notification.received_at ?? notification.date ?? '',
      viewed: notification.viewed,
      link_url: notification.link_url ?? null,
    }));

    setNotifications([...formatted]);
  }, [accessToken, user?.id]);

  const atualizar = useCallback(async () => {
    await getNotifications();
  }, [getNotifications]);

  useEffect(() => {
    if (!accessToken || !user?.id) {
      return;
    }

    void getNotifications();
    const id = setInterval(() => {
      void getNotifications();
    }, 1000 * 60 * 5);
    return () => clearInterval(id);
  }, [accessToken, user?.id, getNotifications]);

  useEffect(() => {
    if (!accessToken || !user?.id || pushRegistered.current) {
      return;
    }

    const registerPushSubscription = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      try {
        const registration = await navigator.serviceWorker.ready;

        const keyRes = await fetch(
          `${Environment.VITE_API_URL}/api/v1/push/vapid-public-key`
        );
        const { publicKey } = await keyRes.json();
        if (!publicKey) return;

        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        const subJson = subscription.toJSON();
        await fetch(`${Environment.VITE_API_URL}/api/v1/push/subscribe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: "include",
          body: JSON.stringify({
            user_id: user.id,
            endpoint: subJson.endpoint,
            p256dh: subJson.keys?.p256dh,
            auth: subJson.keys?.auth,
          }),
        });

        pushRegistered.current = true;
      } catch (err) {
        console.warn("Push subscription failed:", err);
      }
    };

    void registerPushSubscription();
  }, [accessToken, user?.id]);

  const setViewed = (notification: Notification) => {
    notification.viewed = true;
    fetch(`${Environment.VITE_API_URL}/api/v1/set-notification/${notification.id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });
  };

  return (
    <NotificationsContext.Provider value={{ notifications, atualizar, setViewed }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
